import React, { useEffect, useState, useCallback } from "react";
import { useI18n } from "@/i18n";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { CalendarRange, TrendingUp } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from "recharts";

export default function WeeklyView() {
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | incomplete | error

  const load = useCallback(() => {
    setState("loading");
    api
      .get("/fortune/weekly", { params: { lang } })
      .then((r) => {
        if (r.data.profile_complete === false) { setState("incomplete"); setData(r.data); }
        else { setData(r.data); setState("ready"); }
      })
      .catch(() => setState("error"));
  }, [lang]);

  useEffect(() => { load(); }, [load]);

  if (state === "loading") {
    return (
      <div className="space-y-2" data-testid="weekly-loading">
        {[0, 1, 2, 3, 4, 5, 6].map((i) => <Skeleton key={i} className="h-16 w-full rounded-xl" />)}
      </div>
    );
  }
  if (state === "incomplete") {
    return <Card className="border-border"><CardContent className="p-6 text-center text-muted-foreground">{lang === "zh" ? (data?.message_zh) : (data?.message_en)}</CardContent></Card>;
  }
  if (state === "error") {
    return <Card className="border-border"><CardContent className="p-6 text-center"><p className="text-muted-foreground">{t("faq.error")}</p><Button className="mt-3" onClick={load}>{t("faq.retry")}</Button></CardContent></Card>;
  }

  const barColor = (v) => (v >= 75 ? "hsl(var(--brand-gold))" : v >= 50 ? "hsl(var(--brand-bronze))" : "hsl(var(--brand-vermillion))");

  const days = data?.days || [];
  const chartData = days.map((d) => ({
    label: (lang === "zh" ? d.weekday_zh : d.weekday_en) || "",
    date: (d.date || "").slice(5),
    index: Math.max(0, Math.min(100, Number(d.index) || 0)),
  }));
  const avg = chartData.length ? Math.round(chartData.reduce((s, x) => s + x.index, 0) / chartData.length) : 0;

  const ChartTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const p = payload[0].payload;
    return (
      <div className="rounded-lg border border-border bg-popover/95 px-3 py-2 text-xs shadow-lg backdrop-blur" data-testid="weekly-chart-tooltip">
        <div className="font-display font-semibold text-foreground">{p.label} · {p.date}</div>
        <div className="font-mono-num text-[hsl(var(--brand-vermillion))]">{p.index}</div>
      </div>
    );
  };

  return (
    <div data-testid="weekly-view">
      <div className="flex items-center gap-2 mb-4">
        <CalendarRange className="h-5 w-5 text-[hsl(var(--brand-bronze))]" />
        <h2 className="font-display text-xl font-semibold">{t("weekly.title")}</h2>
      </div>

      {/* 本周运程走势图 — 7-day trend */}
      <Card className="border-border mb-4" data-testid="weekly-trend-chart">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="grid h-7 w-7 place-items-center rounded-full bg-[hsl(var(--brand-gold)/0.14)] text-[hsl(var(--brand-bronze))]">
                <TrendingUp className="h-4 w-4" />
              </span>
              <span className="font-display font-semibold text-[hsl(var(--brand-ink))]">
                {lang === "zh" ? "本周运势走势" : "This Week's Trend"}
              </span>
            </div>
            <span className="text-xs text-muted-foreground">
              {lang === "zh" ? "周均" : "Avg"} <span className="font-mono-num font-bold text-[hsl(var(--brand-vermillion))]">{avg}</span>
            </span>
          </div>
          <div className="h-44 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: -18 }}>
                <defs>
                  <linearGradient id="weeklyFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--brand-gold))" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="hsl(var(--brand-gold))" stopOpacity={0.03} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} width={34} />
                <Tooltip content={<ChartTooltip />} cursor={{ stroke: "hsl(var(--brand-bronze))", strokeDasharray: "4 4" }} />
                <ReferenceLine y={avg} stroke="hsl(var(--brand-bronze))" strokeDasharray="5 4" strokeOpacity={0.6} />
                <Area
                  type="monotone" dataKey="index"
                  stroke="hsl(var(--brand-gold))" strokeWidth={2.5}
                  fill="url(#weeklyFill)"
                  dot={{ r: 3, fill: "hsl(var(--brand-vermillion))", strokeWidth: 0 }}
                  activeDot={{ r: 5, fill: "hsl(var(--brand-vermillion))" }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-2.5">
        {(data?.days || []).map((d) => {
          const v = Math.max(0, Math.min(100, Number(d.index) || 0));
          return (
            <Card key={d.date} className="border-border" data-testid={`weekly-day-${d.date}`}>
              <CardContent className="p-3.5 flex items-center gap-4">
                <div className="w-16 text-center shrink-0">
                  <div className="font-display font-semibold text-[hsl(var(--brand-ink))]">{lang === "zh" ? d.weekday_zh : d.weekday_en}</div>
                  <div className="text-[11px] text-muted-foreground font-mono-num">{d.date.slice(5)}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="h-2.5 rounded-full bg-[hsl(var(--secondary))] overflow-hidden">
                    <div className="h-full transition-[width] duration-300" style={{ width: `${v}%`, background: barColor(v) }} />
                  </div>
                  <div className="text-sm text-foreground/85 mt-1.5 truncate">{d.brief}</div>
                </div>
                <div className="w-12 text-right shrink-0">
                  <span className="font-mono-num text-xl font-bold text-[hsl(var(--brand-vermillion))]" data-testid={`weekly-index-${d.date}`}>{v}</span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
